/**
 * Strings of the logistics view "Kompletność" (`logFul.*`): shortages found at fulfilment (ERP difference orders) vs the
 * value of the orders. Merged into the dictionary through `extra.ts`; `logFulEn` is typed against `logFulPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const logFulPl = {
  view: 'Kompletność',
  title: 'Kompletność realizacji',
  description: 'Ile z zamówionego towaru nie pojechało do klienta — braki dopisane przez ERP przy realizacji (ZK różnicowe).',
  stats: {
    ordered: 'Wartość zamówień',
    orderedHint: '{orders} zamówień',
    shortage: 'Wartość braków',
    shortageHint: 'netto, braki przy realizacji',
    pct: 'Braki',
    pctHint: 'wartości zamówień',
    withShortage: 'Zamówienia z brakiem',
    withShortageHint: '{pct} zamówień',
  },
  chart: {
    title: 'Braki dzień po dniu',
    subtitle: 'Dzień zamówienia — braki liczone do zamówienia, którego dotyczą',
    shortage: 'Braki',
    pct: '% wartości',
    ordered: 'Zamówienia',
  },
  routes: {
    title: 'Braki na trasach',
    subtitle: 'Trasa klienta z ERP; najwięcej braków na górze',
    col: { route: 'Trasa', ordered: 'Zamówienia', shortage: 'Braki', pct: '% braków', withShortage: 'Zam. z brakiem' },
    noRoute: 'Bez trasy',
  },
  products: {
    title: 'Najczęściej brakujące towary',
    subtitle: 'Wartość braków w okresie',
    col: { product: 'Towar', shortage: 'Wartość braków', quantity: 'Ilość', orders: 'Zamówienia' },
    unknown: 'Pozycja bez towaru',
    partial: 'Pozycje zamówień są w NEXOPS tylko z ostatnich miesięcy: lista towarów obejmuje {withLines} z {total} braków okresu.',
    none: 'Brak pozycji braków w tym okresie.',
  },
  empty: 'Brak zamówień w tym okresie',
  emptyBody: 'Zmień okres albo sprawdź, czy zamówienia z ERP są zaimportowane.',
  notConfigured: 'ERP nie oznaczył jeszcze żadnych braków',
  notConfiguredBody: 'Braki to zamówienia różnicowe ERP. Wskaż ich wystawcę w ustawieniach integracji (erp:settings --difference-issuer) i zaimportuj zamówienia.',
};

export const logFulEn: Shape<typeof logFulPl> = {
  view: 'Completeness',
  title: 'Fulfilment completeness',
  description: 'How much of the ordered goods did not reach the customer — shortages the ERP adds at fulfilment (difference orders).',
  stats: {
    ordered: 'Order value',
    orderedHint: '{orders} orders',
    shortage: 'Shortage value',
    shortageHint: 'net, found at fulfilment',
    pct: 'Shortages',
    pctHint: 'of the order value',
    withShortage: 'Orders with a shortage',
    withShortageHint: '{pct} of orders',
  },
  chart: {
    title: 'Shortages day by day',
    subtitle: 'Order day — shortages count towards the order they complete',
    shortage: 'Shortages',
    pct: '% of value',
    ordered: 'Orders',
  },
  routes: {
    title: 'Shortages by route',
    subtitle: 'Customer route from the ERP; most shortages first',
    col: { route: 'Route', ordered: 'Orders', shortage: 'Shortages', pct: 'Shortage %', withShortage: 'Orders short' },
    noRoute: 'No route',
  },
  products: {
    title: 'Most often missing products',
    subtitle: 'Shortage value in the period',
    col: { product: 'Product', shortage: 'Shortage value', quantity: 'Quantity', orders: 'Orders' },
    unknown: 'Line without a product',
    partial: 'NEXOPS keeps order lines for recent months only: the product list covers {withLines} of {total} shortages of the period.',
    none: 'No shortage lines in this period.',
  },
  empty: 'No orders in this period',
  emptyBody: 'Change the period or check that the ERP orders are imported.',
  notConfigured: 'The ERP has not marked any shortages yet',
  notConfiguredBody: 'Shortages are ERP difference orders. Set their issuer in the integration settings (erp:settings --difference-issuer) and import the orders.',
};
